import { addToCart } from '@theme/cart-add-v2';
import { CartAddEvent } from '@theme/events';

class ProductHeroV2 extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #labelTimer = null;

  /** @type {number} */
  #scrollFrame = 0;

  /** @type {number} */
  #activeSlide = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const form = this.#form;

    this.querySelectorAll('[data-pack-input], [data-mode-input]').forEach((inputElement) => {
      inputElement.addEventListener('change', () => this.#sync(), { signal });
    });

    this.querySelectorAll('[data-size-input]').forEach((inputElement) => {
      inputElement.addEventListener('change', () => this.#selectPackForSize(inputElement), { signal });
    });

    form?.addEventListener('submit', (event) => this.#submit(event), { signal });

    this.querySelectorAll('[data-tab]').forEach((tabButton) => {
      tabButton.addEventListener('click', () => this.#selectTab(tabButton), { signal });
      tabButton.addEventListener('keydown', (event) => this.#onTabKey(event), { signal });
    });

    const trackElement = this.querySelector('[data-gallery-track]');
    trackElement?.addEventListener('scroll', () => this.#onGalleryScroll(), { passive: true, signal });
    this.querySelectorAll('[data-gallery-go]').forEach((navButton) => {
      navButton.addEventListener('click', () => this.#goToSlide(Number(navButton.getAttribute('data-gallery-go'))), { signal });
    });
    this.querySelectorAll('[data-gallery-step]').forEach((arrowButton) => {
      arrowButton.addEventListener(
        'click',
        () => this.#goToSlide(this.#activeSlide + Number(arrowButton.getAttribute('data-gallery-step'))),
        { signal }
      );
    });

    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    if (this.#labelTimer !== null) clearTimeout(this.#labelTimer);
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
  }

  get #form() {
    const form = document.getElementById(this.dataset.formId || '');
    return form instanceof HTMLFormElement ? form : null;
  }

  get #isSubscribe() {
    const modeInput = this.querySelector('[data-mode-input]:checked');
    return modeInput instanceof HTMLInputElement && modeInput.value === 'subscribe';
  }

  #sync() {
    const packInput = this.querySelector('[data-pack-input]:checked');
    if (!(packInput instanceof HTMLInputElement)) return;

    const sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    if (sellingPlanInput instanceof HTMLInputElement) {
      const sellingPlan = packInput.dataset.sellingPlan || '';
      const useSellingPlan = this.#isSubscribe && sellingPlan !== '';
      sellingPlanInput.value = useSellingPlan ? sellingPlan : '';
      sellingPlanInput.disabled = !useSellingPlan;
    }

    const subscribePrice = this.querySelector('[data-option-price="subscribe"]');
    if (subscribePrice instanceof HTMLElement) subscribePrice.textContent = packInput.dataset.priceSubscribe || '';

    const onetimePrice = this.querySelector('[data-option-price="onetime"]');
    if (onetimePrice instanceof HTMLElement) onetimePrice.textContent = packInput.dataset.priceOnetime || '';

    const compareElement = this.querySelector('[data-option-compare]');
    if (compareElement instanceof HTMLElement) compareElement.textContent = packInput.dataset.priceRegular || '';

    this.querySelectorAll('[data-pack-savings]').forEach(
      /** @param {Element} savingsElement */ (savingsElement) => {
        savingsElement.textContent = packInput.dataset.saveSubscribe || '';
      }
    );
  }

  /** @param {Element} sizeInput */
  #selectPackForSize(sizeInput) {
    const sizeInputs = [...this.querySelectorAll('[data-size-input]')];
    const packInput = this.querySelectorAll('[data-pack-input]')[sizeInputs.indexOf(sizeInput)];
    if (!(packInput instanceof HTMLInputElement) || packInput.disabled || packInput.checked) return;
    packInput.checked = true;
    this.#sync();
  }

  /** @param {SubmitEvent} event */
  async #submit(event) {
    event.preventDefault();
    const form = this.#form;
    const atcButton = this.querySelector('[data-atc-button]');
    const labelElement = this.querySelector('[data-atc-label]');
    const errorElement = this.querySelector('[data-atc-error]');
    if (!form || !(atcButton instanceof HTMLButtonElement) || !(labelElement instanceof HTMLElement)) return;

    this.#sync();
    const defaultLabel = labelElement.dataset.defaultLabel || labelElement.textContent || '';
    labelElement.dataset.defaultLabel = defaultLabel;
    atcButton.disabled = true;
    labelElement.textContent = atcButton.dataset.addingLabel || 'Adding...';
    if (errorElement instanceof HTMLElement) errorElement.hidden = true;

    const formData = new FormData(form);
    const packInput = this.querySelector('[data-pack-input]:checked');
    if (packInput instanceof HTMLInputElement) {
      if (this.#isSubscribe && packInput.dataset.subscriptionVariant) {
        formData.set('id', packInput.dataset.subscriptionVariant);
        if (packInput.dataset.packLabel) formData.set('properties[Size]', packInput.dataset.packLabel);
      }
    }
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((cartItems) => (cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const response = await addToCart(formData, { Accept: 'text/html' });
      const data = await response.json();

      if (data.status) {
        if (errorElement instanceof HTMLElement) {
          errorElement.textContent = data.description || data.message || '';
          errorElement.hidden = false;
        }
        this.#resetLabel(atcButton, labelElement, defaultLabel, 0);
        return;
      }

      labelElement.textContent = atcButton.dataset.addedLabel || 'Added';
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      this.dispatchEvent(
        new CartAddEvent(cart, String(formData.get('id')), {
          source: 'product-hero-v2',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      atcButton.disabled = false;
      this.#resetLabel(atcButton, labelElement, defaultLabel, 1800);
    } catch (error) {
      console.error(error);
      this.#resetLabel(atcButton, labelElement, defaultLabel, 0);
    }
  }

  /**
   * @param {HTMLButtonElement} atcButton
   * @param {HTMLElement} labelElement
   * @param {string} defaultLabel
   * @param {number} delay
   */
  #resetLabel(atcButton, labelElement, defaultLabel, delay) {
    if (this.#labelTimer !== null) clearTimeout(this.#labelTimer);
    this.#labelTimer = setTimeout(() => {
      labelElement.textContent = defaultLabel;
      atcButton.disabled = false;
      this.#labelTimer = null;
    }, delay);
  }

  /** @param {Element} tabButton */
  #selectTab(tabButton) {
    this.querySelectorAll('[data-tab]').forEach((otherTab) => {
      const isActive = otherTab === tabButton;
      otherTab.setAttribute('aria-selected', String(isActive));
      otherTab.setAttribute('tabindex', isActive ? '0' : '-1');
      const panelElement = document.getElementById(otherTab.getAttribute('aria-controls') || '');
      if (panelElement) panelElement.hidden = !isActive;
    });
  }

  /** @param {KeyboardEvent} event */
  #onTabKey(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabButtons = [...this.querySelectorAll('[data-tab]')];
    const currentIndex = tabButtons.indexOf(/** @type {Element} */ (event.currentTarget));
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const nextTab = tabButtons[(currentIndex + step + tabButtons.length) % tabButtons.length];
    if (!(nextTab instanceof HTMLElement)) return;
    event.preventDefault();
    this.#selectTab(nextTab);
    nextTab.focus();
  }

  #onGalleryScroll() {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      const trackElement = this.querySelector('[data-gallery-track]');
      if (!(trackElement instanceof HTMLElement)) return;
      const slides = [...trackElement.querySelectorAll('[data-gallery-slide]')];
      const trackCenter = trackElement.getBoundingClientRect().left + trackElement.clientWidth / 2;
      let activeIndex = 0;
      let closestDistance = Infinity;
      slides.forEach((slideElement, index) => {
        const rect = slideElement.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });
      this.#setActive(activeIndex);
    });
  }

  /** @param {number} index */
  #setActive(index) {
    this.#activeSlide = index;
    this.querySelectorAll('[data-gallery-go]').forEach((navButton) => {
      const isActive = Number(navButton.getAttribute('data-gallery-go')) === index;
      navButton.classList.toggle('is-active', isActive);
      if (isActive) navButton.setAttribute('aria-current', 'true');
      else navButton.removeAttribute('aria-current');
    });
  }

  /** @param {number} index */
  #goToSlide(index) {
    const trackElement = this.querySelector('[data-gallery-track]');
    if (!(trackElement instanceof HTMLElement)) return;
    const slides = trackElement.querySelectorAll('[data-gallery-slide]');
    const slideIndex = (index + slides.length) % slides.length;
    const slideElement = slides[slideIndex];
    if (!(slideElement instanceof HTMLElement)) return;
    this.#setActive(slideIndex);
    const offset = slideElement.offsetLeft - (trackElement.clientWidth - slideElement.clientWidth) / 2;
    trackElement.scrollTo({ left: offset, behavior: 'smooth' });
  }
}

if (!customElements.get('product-hero-v2')) {
  customElements.define('product-hero-v2', ProductHeroV2);
}
